import { useEffect, useRef, useState } from 'react';
import { Copy, Mail, ExternalLink, FileText, FilePen, PencilLine, RefreshCw } from 'lucide-react';
import Panel from './Panel.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { Input } from '@/components/ui/input.jsx';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { getHangarSubmission, regenerateHangarPacket } from '@/services/deals.service.js';
import HangarTermsSheet from '@/components/sheets/HangarTermsSheet.jsx';
import HangarApplicationSheet from '@/components/sheets/HangarApplicationSheet.jsx';
import LogHangarQuoteSheet from '@/components/sheets/LogHangarQuoteSheet.jsx';
import QuoteSourceDocs from './QuoteSourceDocs.jsx';
import { getQuoteFull } from '@/services/quotes.service.js';
import { fmtMoney } from '@/lib/quoteFields.js';

// Cross-panel refresh signal: HangarLocationsPanel listens for this so an
// Edit-submission save refreshes its merged location data without coupling
// the two panels through the workspace fetch.
export const HANGAR_SUBMISSION_UPDATED_EVENT = 'hangar-submission-updated';

// TRACON Submission panel (hangar/property) — Phase 1 of HANGAR-SUBMISSION-
// FIXES §A: the broker sends from their OWN mailbox. TRACON's job is one-click
// prep: the generated packet PDF (View / download), an editable narrative
// (subject + body, prefilled), and the resolved carrier recipients to copy.
// The TRACON direct-send path is retired (server returns 410 unless the
// HANGAR_TRACON_SEND Phase-2 flag is set), so there is no Send button here.
// NHIP links out to the Gallagher portal; ABC routes by effective-date day.

function copyValue(value, label) {
  if (typeof navigator === 'undefined' || !navigator.clipboard) {
    toast.error('Clipboard unavailable in this browser');
    return;
  }
  navigator.clipboard
    .writeText(value)
    .then(() => toast.success(`Copied ${label || value}`))
    .catch(() => toast.error('Copy failed'));
}

function usDate(ymd) {
  if (typeof ymd !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(ymd)) return '';
  return `${ymd.slice(5, 7)}/${ymd.slice(8, 10)}/${ymd.slice(0, 4)}`;
}

function fmtLimit(v) {
  const n = Number(String(v == null ? '' : v).replace(/[$,\s]/g, ''));
  if (!isFinite(n) || n <= 0) return '';
  return '$' + n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function defaultSubject(data) {
  const insured = data?.insured || '';
  const op = data?.operation_label || '';
  return `NDEMNIFLY Submission — ${insured || 'Applicant'}${op ? ` — ${op}` : ''}`;
}

// One "Label $X" list for a location's coverages (+ misc coverage when present).
function locationCoverageParts(loc) {
  const parts = (loc?.coverages || [])
    .map((c) => `${c.label} ${fmtLimit(c.limit)}`.trim())
    .filter(Boolean);
  if (loc?.misc_coverage && (loc.misc_coverage.description || loc.misc_coverage.limit)) {
    const m = `${loc.misc_coverage.description || 'Miscellaneous Coverage'} ${fmtLimit(loc.misc_coverage.limit)}`.trim();
    if (m) parts.push(m);
  }
  return parts;
}

function defaultBody(data) {
  const insured = data?.insured || 'the applicant';
  const op = data?.operation_label || '';
  const eff = usDate(data?.effective_date || '');

  // Multi-location: one line per location so no location's coverage can be
  // missed (2026-07: coverage_lines carried only the first location that had
  // each coverage, and a carrier quoted Location 1 only). Single location
  // keeps the original one-line form.
  const locs = Array.isArray(data?.locations) ? data.locations : [];
  let covsBlock = '';
  if (locs.length > 1) {
    const lines = locs
      .map((l, i) => {
        const parts = locationCoverageParts(l);
        if (parts.length === 0) return '';
        const tag = l?.airport ? `Location ${i + 1} (${l.airport})` : `Location ${i + 1}`;
        return `${tag}: ${parts.join(' · ')}`;
      })
      .filter(Boolean);
    if (lines.length > 0) {
      covsBlock = `Requested coverages (${locs.length} locations):\n${lines.join('\n')}`;
    }
  } else {
    const covs = (data?.coverage_lines || [])
      .map((c) => `${c.label} ${fmtLimit(c.limit)}`.trim())
      .filter(Boolean)
      .join(' · ');
    if (covs) covsBlock = `Requested coverages: ${covs}.`;
  }

  return [
    'Hello,',
    `Please find attached the NDEMNIFLY hangar / property submission for ${insured}${op ? ` (${op})` : ''}.`,
    eff ? `Proposed effective date: ${eff}.` : '',
    covsBlock,
    'The completed application is attached as a PDF. Please advise on appetite and quoted terms at your convenience.',
    'Thank you,\nDavid Asman\nNDEMNIFLY Aviation Insurance\n706-205-0200',
  ].filter(Boolean).join('\n\n');
}

function mailtoHref(email, subject, body) {
  const params = [];
  if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
  if (body) params.push(`body=${encodeURIComponent(body)}`);
  return `mailto:${email}${params.length ? `?${params.join('&')}` : ''}`;
}

function EmailRecipientRow({ recipient, effectiveDate, subject, body }) {
  const isAbc = recipient.carrier === 'ABC';
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-card border border-border px-3 py-2">
      <Badge variant="outline" className="font-mono text-[10px] uppercase">{recipient.carrier}</Badge>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-text">{recipient.name}</span>
          {recipient.ambiguous ? (
            <Badge variant="warning" className="text-[10px]">confirm recipient</Badge>
          ) : isAbc && effectiveDate ? (
            <span className="text-[11px] text-text-dim">routed by effective {effectiveDate}</span>
          ) : null}
        </div>
        <div className="truncate text-xs text-text-muted">{recipient.email}</div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => copyValue(recipient.email, recipient.email)}
        title="Copy email address"
      >
        <Copy className="h-3.5 w-3.5" />
      </Button>
      <Button asChild variant="outline" size="sm" title="Compose in your mail client with the narrative below — attach the downloaded PDF before sending">
        <a href={mailtoHref(recipient.email, subject, body)}>
          <Mail className="mr-1 h-3.5 w-3.5" />
          Compose
        </a>
      </Button>
    </div>
  );
}

function PortalRecipientRow({ recipient }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-card border border-border px-3 py-2">
      <Badge variant="outline" className="font-mono text-[10px] uppercase">{recipient.carrier}</Badge>
      <div className="min-w-0 flex-1">
        <span className="text-sm font-medium text-text">Portal submission</span>
        <div className="truncate text-xs text-text-muted">Enter the data in the broker portal; never emailed.</div>
      </div>
      {recipient.url ? (
        <Button asChild variant="outline" size="sm">
          <a href={recipient.url} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="mr-1 h-3.5 w-3.5" />
            Open NHIP portal
          </a>
        </Button>
      ) : null}
    </div>
  );
}

// One-line human summary of the entered quoted terms for the panel row.
function termsSummary(terms) {
  if (!terms || typeof terms !== 'object') return '';
  const parts = [];
  if (terms.liability_limit === 'other') {
    const amt = (v) => (v != null ? fmtMoney(Number(v)) : 'not stated');
    parts.push(`liability ${amt(terms.other_per_occurrence)} / ${amt(terms.other_aggregate)}`);
  } else if (terms.liability_limit) parts.push(`liability ${terms.liability_limit}`);
  if (terms.protection_class) parts.push(`protection class ${terms.protection_class}`);
  const lineCount = Object.keys(terms.coverages || {}).length;
  if (lineCount > 0) parts.push(`${lineCount} coverage line${lineCount === 1 ? '' : 's'}`);
  if (terms.updates && Object.keys(terms.updates).length > 0) parts.push('building updates');
  return parts.join(' · ');
}

export default function SubmissionPanel({ dealId }) {
  const [state, setState] = useState({ loading: !!dealId, error: null, data: null });
  const dataRef = useRef(null);
  const [regenerating, setRegenerating] = useState(false);
  const [reissueOpen, setReissueOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  // Edit-in-place: the full quote (with lines) being edited, or null = new.
  const [editQuote, setEditQuote] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  // Edit-submission saved: re-pull the merged application (overrides applied
  // server-side), adopt the regenerated packet link, and tell sibling panels.
  function handleApplicationSaved(res) {
    if (res?.link) {
      setState((s) => ({ ...s, data: { ...(s.data || {}), packet_link: res.link } }));
    }
    if (dealId) {
      getHangarSubmission(dealId)
        .then((d) => setState((s) => ({ ...s, data: d })))
        .catch(() => {});
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(HANGAR_SUBMISSION_UPDATED_EVENT, { detail: { dealId } }));
    }
  }

  // A bound deal's packet is the SIGNED one. The plain call 409s there; the
  // broker is asked once, in a dialog, and the re-issue rides the same route
  // with reissue_signed set (2026-09-14: this is the handle for the bound
  // re-render, which existed only as a script before).
  function handleRegenerate({ reissueSigned = false } = {}) {
    if (!dealId || regenerating) return;
    setRegenerating(true);
    regenerateHangarPacket(dealId, { reissueSigned })
      .then((res) => {
        const link = res?.link || null;
        if (link) {
          setState((s) => ({ ...s, data: { ...(s.data || {}), packet_link: link } }));
          toast.success(reissueSigned ? 'Signed packet re-issued' : 'Packet regenerated');
        } else {
          toast.error(res?.error || 'Regenerate failed');
        }
      })
      .catch((err) => {
        if (!reissueSigned && err?.status === 409 && /reissue_signed/.test(String(err?.body?.error || ''))) {
          setReissueOpen(true);
          return;
        }
        toast.error(err?.body?.error || err?.message || 'Regenerate failed');
      })
      .finally(() => setRegenerating(false));
  }

  useEffect(() => {
    let aborted = false;
    if (!dealId) {
      setState({ loading: false, error: null, data: null });
      return undefined;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    getHangarSubmission(dealId)
      .then((d) => {
        if (aborted) return;
        setState({ loading: false, error: null, data: d });
        // Prefill the narrative draft once per deal — broker edits stick until
        // the focused deal changes.
        setSubject(defaultSubject(d));
        setBody(defaultBody(d));
      })
      .catch((err) => {
        if (!aborted) setState({ loading: false, error: err, data: null });
      });
    return () => {
      aborted = true;
    };
  }, [dealId]);

  // Cross-panel refresh: EditNamedInsuredSheet (cascade) and this panel's own
  // Edit-submission save dispatch HANGAR_SUBMISSION_UPDATED_EVENT. Re-pull the
  // submission so the packet link and insured reflect the change, and refresh
  // the narrative draft ONLY when the broker hasn't edited it (an untouched
  // draft still equals the previous data's defaults).
  useEffect(() => {
    if (!dealId || typeof window === 'undefined') return undefined;
    function onSubmissionUpdated(e) {
      if (e?.detail?.dealId && String(e.detail.dealId) !== String(dealId)) return;
      getHangarSubmission(dealId)
        .then((d) => {
          const prev = dataRef.current;
          setState((s) => ({ ...s, data: d }));
          setSubject((cur) => (prev && cur === defaultSubject(prev) ? defaultSubject(d) : cur));
          setBody((cur) => (prev && cur === defaultBody(prev) ? defaultBody(d) : cur));
        })
        .catch(() => {});
    }
    window.addEventListener(HANGAR_SUBMISSION_UPDATED_EVENT, onSubmissionUpdated);
    return () => window.removeEventListener(HANGAR_SUBMISSION_UPDATED_EVENT, onSubmissionUpdated);
  }, [dealId]);

  const { loading, error, data } = state;
  dataRef.current = data;
  const recipients = data?.recipients || [];
  const emailRecipients = recipients.filter((r) => r.mode === 'email');
  const portalRecipients = recipients.filter((r) => r.mode === 'portal');
  const packetLink = data?.packet_link || null;
  const effectiveDate = data?.effective_date || null;

  return (
    <Panel title="Submission">
      {loading ? (
        <p className="text-xs italic text-text-dim">Loading submission…</p>
      ) : error ? (
        <p className="text-xs italic text-text-dim">Could not load submission details.</p>
      ) : (
        <div className="space-y-4">
          {/* Generated packet */}
          <div>
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <h3 className="text-[11px] font-semibold uppercase tracking-wide text-text-dim">Application packet</h3>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditOpen(true)}
                  title="Correct or complete the application data (named insured, locations, requested liability, history) — edits layer over the client's intake and the packet regenerates"
                >
                  <FilePen className="mr-1 h-3.5 w-3.5" />
                  Edit submission
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRegenerate()}
                  disabled={regenerating}
                  title="Rebuild the packet PDF from the latest intake + quoted terms"
                >
                  <RefreshCw className={`mr-1 h-3.5 w-3.5${regenerating ? ' animate-spin' : ''}`} />
                  {regenerating ? 'Regenerating…' : packetLink ? 'Regenerate packet' : 'Generate packet'}
                </Button>
              </div>
            </div>
            <Dialog open={reissueOpen} onOpenChange={setReissueOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Re-issue the signed packet?</DialogTitle>
                  <DialogDescription>
                    This deal is bound, so its packet is the signed application. Re-issuing rebuilds it from the bound quote (carrier, quoted limits), the account's additional interests and the application on file, and reprints the client's signature with its original timestamp. Nothing is re-signed. A new version files in WorkDrive.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="ghost" size="sm" onClick={() => setReissueOpen(false)}>Cancel</Button>
                  <Button size="sm" onClick={() => { setReissueOpen(false); handleRegenerate({ reissueSigned: true }); }}>Re-issue signed packet</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            {packetLink ? (
              <div className="flex flex-wrap items-center gap-2 rounded-card border border-border px-3 py-2">
                <FileText className="h-4 w-4 shrink-0 text-text-muted" />
                <span className="min-w-0 flex-1 truncate text-xs text-text-muted">{packetLink}</span>
                <Button variant="ghost" size="sm" onClick={() => copyValue(packetLink, 'packet link')} title="Copy packet link (broker-internal — carriers can't open WorkDrive links)">
                  <Copy className="h-3.5 w-3.5" />
                </Button>
                <Button asChild variant="outline" size="sm">
                  <a href={packetLink} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="mr-1 h-3.5 w-3.5" />
                    View / download PDF
                  </a>
                </Button>
              </div>
            ) : (
              <p className="text-xs italic text-text-dim">
                No packet on file yet. It is generated automatically when the hangar intake is submitted.
              </p>
            )}
          </div>

          {/* Quotes (§7 Slice 5) — log a carrier hangar quote into the canonical
              store so it presents in the client's Tower and is bindable. */}
          <div>
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <h3 className="text-[11px] font-semibold uppercase tracking-wide text-text-dim">Quotes</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setEditQuote(null); setQuoteOpen(true); }}
                title="Enter a carrier's hangar/property quote — presents in the client's Tower and is bindable"
              >
                <FilePen className="mr-1 h-3.5 w-3.5" />
                Log hangar quote
              </Button>
            </div>
            {Array.isArray(data?.quotes) && data.quotes.length > 0 ? (
              <div className="mb-2 space-y-1">
                {data.quotes.map((q) => (
                  <div key={q.id} className="flex items-center justify-between gap-2 rounded-card border border-border px-3 py-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm text-text">{q.carrier}</p>
                      <p className="text-[11px] text-text-dim">
                        {[
                          q.total_premium != null ? `$${Number(q.total_premium).toLocaleString('en-US')} base` : null,
                          q.term_months ? `${q.term_months} mo` : null,
                          q.status,
                          q.client_visible ? 'client-visible' : 'hidden from client',
                        ].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={['declined', 'expired'].includes(q.status)}
                      onClick={async () => {
                        try {
                          const res = await getQuoteFull(q.id);
                          if (!res?.success) { toast.error(res?.error || 'Could not load the quote'); return; }
                          setEditQuote(res.quote);
                          setQuoteOpen(true);
                        } catch (e) { toast.error(e?.body?.error || e?.message || 'Could not load the quote'); }
                      }}
                    >
                      <PencilLine className="mr-1 h-3.5 w-3.5" />
                      Edit
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs italic text-text-dim">
                Enter a carrier quote (building schedule, contents, liability, taxes and fees, premium). &quot;Make visible to client&quot; presents it in Tower.
              </p>
            )}
            <QuoteSourceDocs dealId={dealId} />
          </div>

          {/* Quoted terms (§9) — broker-entered carrier terms applied on regenerate */}
          <div>
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <h3 className="text-[11px] font-semibold uppercase tracking-wide text-text-dim">Quoted terms</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setTermsOpen(true)}
                title="Enter the carrier's quoted property terms (coinsurance, deductible, special forms, occupancy, protection class, liability tier)"
              >
                <PencilLine className="mr-1 h-3.5 w-3.5" />
                {termsSummary(data?.hangar_terms) ? 'Edit quoted terms' : 'Enter quoted terms'}
              </Button>
            </div>
            {termsSummary(data?.hangar_terms) ? (
              <p className="text-xs text-text-muted">{termsSummary(data?.hangar_terms)} — regenerate the packet to apply.</p>
            ) : (
              <p className="text-xs italic text-text-dim">
                No quoted terms entered yet. The packet&apos;s term columns render blank until a carrier quotes.
              </p>
            )}
          </div>

          {/* Email narrative (Phase 1) — broker-editable draft for the Gmail send */}
          <div>
            <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-dim">Email narrative</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Subject"
                  className="h-9 flex-1 text-sm"
                />
                <Button variant="ghost" size="sm" onClick={() => copyValue(subject, 'subject')} title="Copy subject">
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="flex items-start gap-2">
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={8}
                  className="flex-1 rounded-card border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-accent/40"
                />
                <Button variant="ghost" size="sm" onClick={() => copyValue(body, 'email body')} title="Copy email body">
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>

          {/* Recipients */}
          <div>
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <h3 className="text-[11px] font-semibold uppercase tracking-wide text-text-dim">Send to</h3>
              {emailRecipients.length > 1 ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyValue(emailRecipients.map((r) => r.email).join(', '), 'all recipient emails')}
                  title="Copy every carrier email, comma-separated"
                >
                  <Copy className="mr-1 h-3.5 w-3.5" />
                  Copy all
                </Button>
              ) : null}
            </div>
            {recipients.length === 0 ? (
              <p className="text-xs italic text-text-dim">No carrier recipients resolved.</p>
            ) : (
              <div className="space-y-2">
                {emailRecipients.map((r, i) => (
                  <EmailRecipientRow
                    key={`${r.carrier}-${r.email}-${i}`}
                    recipient={r}
                    effectiveDate={effectiveDate}
                    subject={subject}
                    body={body}
                  />
                ))}
                {portalRecipients.map((r, i) => (
                  <PortalRecipientRow key={`${r.carrier}-portal-${i}`} recipient={r} />
                ))}
              </div>
            )}
            <p className="mt-2 text-[11px] text-text-dim">
              Download the PDF, then send the narrative from your own mailbox with the PDF attached — it lands in your sent mail. WorkDrive links are login-walled; never paste one into a carrier email.
            </p>
          </div>
        </div>
      )}
      <HangarTermsSheet
        dealId={dealId}
        open={termsOpen}
        onOpenChange={setTermsOpen}
        coverageLines={data?.coverage_lines || []}
        initialTerms={data?.hangar_terms || {}}
        intakeUpdates={data?.intake_updates || null}
        onSaved={(saved) => setState((s) => ({ ...s, data: { ...(s.data || {}), hangar_terms: saved } }))}
      />
      <LogHangarQuoteSheet
        dealId={dealId}
        open={quoteOpen}
        onOpenChange={(o) => { setQuoteOpen(o); if (!o) setEditQuote(null); }}
        quote={editQuote}
        onSaved={() => window.dispatchEvent(new CustomEvent(HANGAR_SUBMISSION_UPDATED_EVENT, { detail: { dealId } }))}
      />
      <HangarApplicationSheet
        dealId={dealId}
        open={editOpen}
        onOpenChange={setEditOpen}
        application={data?.application || null}
        onSaved={handleApplicationSaved}
      />
    </Panel>
  );
}
